import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  Grid,
  Card,
  CardContent,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as ValidateIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiService } from '../services/api';
import { renderCompactLink } from '../utils/formatters';

// Helper function to truncate text
const truncateText = (text: string, maxLength: number = 30) => {
  if (!text) return '-';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const OutreachValidationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');

  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageAlert, setPageAlert] = useState<{
    severity: 'success' | 'info' | 'error';
    message: string;
  } | null>(null);

  const showPageAlert = (message: string, severity: 'success' | 'info' | 'error' = 'info') => {
    setPageAlert({ message, severity });
  };

  const [displayCount, setDisplayCount] = useState(100);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  const [selectedDomains, setSelectedDomains] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showBackWarning, setShowBackWarning] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [dailyLimit, setDailyLimit] = useState(4500);
  const [validationStats, setValidationStats] = useState<{
    creditsUsedToday: number;
    dailyLimit: number;
    remaining: number;
    today: string;
  } | null>(null);
  const [scheduleName, setScheduleName] = useState('');
  
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Credit balance state
  const [credits, setCredits] = useState<{
    daily: number;
    instant: number;
    loading: boolean;
  }>({
    daily: 0,
    instant: 0,
    loading: false
  });

  useEffect(() => {
    if (!sessionId) {
      setError("Session ID not found in URL.");
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Check if we have resumedDomainIds in URL (from resume session)
        const resumedDomainIds = searchParams.get('resumedDomainIds');
        console.log('[Validation Page] Session ID:', sessionId);
        console.log('[Validation Page] Resumed Domain IDs from URL:', resumedDomainIds);
        // Use the new validation endpoint that only aggregates emails without re-enriching
        const result = await ApiService.getValidationData(sessionId, resumedDomainIds || undefined);
        console.log('[Validation Page] Received domains:', result.length);
        console.log('[Validation Page] Domain IDs:', result.map(d => d.id).join(', '));
        setData(result);
      } catch (err: any) {
        let errorMessage = "Failed to fetch validation data.";
        
        if (err instanceof Error) {
          // Check if it's a network/server error
          if (err.message.includes('Failed to fetch') || 
              err.message.includes('NetworkError') || 
              err.message.includes('Network request failed') || 
              err.name === 'TypeError') {
            errorMessage = 'Server not running. Please start the server';
          } else {
            errorMessage = err.message || errorMessage;
          }
        }
        
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [sessionId, searchParams]);

  // Fetch credit balance on mount
  const fetchValidationStats = async () => {
    try {
      const stats = await ApiService.getValidationStats();
      setValidationStats(stats);
    } catch (err) {
      console.error('Error fetching validation stats:', err);
    }
  };

  useEffect(() => {
    fetchCredits();
    fetchValidationStats();
  }, []);

  const fetchCredits = async () => {
    setCredits(prev => ({ ...prev, loading: true }));
    try {
      const balance = await ApiService.checkAccountBalance();
      setCredits({
        daily: balance.remaining_daily_credits,
        instant: balance.remaining_instant_credits,
        loading: false
      });
    } catch (err) {
      console.error('Error fetching credits:', err);
      setCredits(prev => ({ ...prev, loading: false }));
    }
  };

  const summaryStats = React.useMemo(() => {
    const totalDomains = data.length;
    const totalEmails = data.reduce((sum, d) => sum + (d.email_count || d.emailCount || 0), 0);
    
    return {
      totalDomains,
      totalEmails,
      totalNewEmails: 0, // Placeholder
      totalLostEmails: 0, // Placeholder
      totalDeliverable: 0, // Placeholder
      totalRisky: 0, // Placeholder
      totalUnknown: 0, // Placeholder
    };
  }, [data]);

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    setTimeout(() => {
        setDisplayCount(prev => Math.min(prev + 100, data.length));
      setIsLoadingMore(false);
    }, 100);
    };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDomains(new Set());
    } else {
      const allIds = data.map(d => d.id);
      setSelectedDomains(new Set(allIds));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectDomain = (id: number) => {
    const newSelected = new Set(selectedDomains);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedDomains(newSelected);
    setSelectAll(newSelected.size === displayCount);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortField) return data;
    
    return [...data].sort((a: any, b: any) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDirection]);

  const handleBack = () => {
    setShowBackWarning(true);
  };

  const confirmBack = () => {
    setShowBackWarning(false);
    // Go back to hub to avoid the "scraping page" loop
    navigate('/outreach');
  };

  const handleSaveForLater = async () => {
    setPageAlert(null);
    if (selectedDomains.size === 0) {
      showPageAlert('Please select at least one domain to save for later.');
      return;
    }

    if (!sessionId) {
      showPageAlert('Session ID not found.', 'error');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const domainIds = Array.from(selectedDomains);
      const result = await ApiService.saveForLater(sessionId, domainIds, undefined, 'validation');
      
      // Show success message
      showPageAlert(
        `Successfully saved ${result.savedDomains} domain(s) for later. They will no longer appear in this validation view.`,
        'success'
      );
      
      // Remove saved domains from local state
      setData(prevData => prevData.filter(d => !selectedDomains.has(d.id)));
      
      // Clear selection
      setSelectedDomains(new Set());
      setSelectAll(false);
      
    } catch (err: any) {
      console.error('[Save for Later] Error:', err);
      setError(err.message || 'Failed to save domains for later. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAllForLater = async () => {
    setPageAlert(null);
    if (data.length === 0) {
      showPageAlert('No domains to save.');
      return;
    }

    if (!sessionId) {
      showPageAlert('Session ID not found.', 'error');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const allDomainIds = data.map(d => d.id);
      const result = await ApiService.saveForLater(sessionId, allDomainIds, undefined, 'validation');
      
      showPageAlert(
        `Successfully saved all ${result.savedDomains} domain(s) for later.`,
        'success'
      );
      
      setData([]);
      setSelectedDomains(new Set());
      setSelectAll(false);
      
    } catch (err: any) {
      console.error('[Save All for Later] Error:', err);
      setError(err.message || 'Failed to save all domains for later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSelected = () => {
    setPageAlert(null);
    if (selectedDomains.size === 0) {
      showPageAlert('Please select at least one domain to delete.');
      return;
    }

    if (!sessionId) {
      showPageAlert('Session ID not found.', 'error');
      return;
    }

    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirmation(false);
    setPageAlert(null);

    if (!sessionId) {
      showPageAlert('Session ID not found.', 'error');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const domainIds = Array.from(selectedDomains);
      const result = await ApiService.deleteDomains(sessionId, domainIds);
      
      showPageAlert(
        `Successfully deleted ${result.deletedDomains} domain(s) and ${result.deletedEmails} email(s).`,
        'success'
      );
      
      // Refresh data to remove deleted domains
      // Preserve resumedDomainIds if we're in resumed session mode
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const updatedData = await ApiService.getValidationData(sessionId, resumedDomainIds || undefined);
      setData(updatedData);
      
      // Clear selection
      setSelectedDomains(new Set());
      setSelectAll(false);
      
    } catch (err: any) {
      console.error('[Delete] Error:', err);
      setError(err.message || 'Failed to delete domains. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleValidateSelected = async () => {
    setPageAlert(null);
    if (selectedDomains.size === 0) {
      showPageAlert('Please select at least one domain to validate.');
      return;
    }

    if (!sessionId) {
      showPageAlert('Session ID not found.', 'error');
      return;
    }

    setIsValidating(true);
    setValidationProgress('Preparing emails...');
    setError(null);

    let pollInterval: NodeJS.Timeout | null = null;

    try {
      // Get selected domain objects
      const selectedDomainObjects = data.filter(d => selectedDomains.has(d.id));
      const domains = selectedDomainObjects.map(d => d.website).filter(Boolean);
      
      // Fetch all emails for selected domains
      setValidationProgress('Fetching emails from selected domains...');
      const emailPromises = domains.map(domain => 
        ApiService.getEmailsByDomain(domain, sessionId || undefined)
      );
      const emailArrays = await Promise.all(emailPromises);
      const allEmails = emailArrays.flat().filter((email, index, self) => 
        email && self.indexOf(email) === index // Remove duplicates
      );

      if (allEmails.length === 0) {
        showPageAlert('No emails found for selected domains.');
        setIsValidating(false);
        setValidationProgress('');
        return;
      }

      // Step 1: Create bulk validation task
      setValidationProgress(`Creating bulk validation task for ${allEmails.length} emails...`);
      const result = await ApiService.validateEmails(allEmails, sessionId || undefined);
      
      if (result.status === 'task_created') {
        const taskId = result.task_id;
        setValidationProgress(`Task created! Processing ${result.count_processing} emails (${result.count_duplicates_removed} duplicates removed)...`);
        
        // Step 2: Poll for completion
        pollInterval = setInterval(async () => {
          try {
            const statusResult = await ApiService.checkValidationStatus(taskId, sessionId);
            
            if (statusResult.status === 'running' || statusResult.status === 'waiting') {
              setValidationProgress(`Validating... ${statusResult.count_checked}/${statusResult.count_total} (${Math.round(statusResult.progress)}%)`);
            } else if (statusResult.status === 'error_temporary') {
              // Temporary error (e.g., API server down) - continue polling but show warning
              setValidationProgress(`⚠️ ${statusResult.message || 'Temporary API error - continuing to check status...'}`);
              console.warn('[Validation] Temporary API error, continuing to poll:', statusResult.error);
              // Don't stop polling - the task may still be processing on Reoon's side
            } else if (statusResult.status === 'completed') {
              if (pollInterval) clearInterval(pollInterval);
              setIsValidating(false);
              setValidationProgress('');
              
              // Show summary
              const summaryMessage = `
Validation Complete!
Total: ${statusResult.summary.total}
Deliverable: ${statusResult.summary.deliverable}
Invalid: ${statusResult.summary.invalid}
Risky: ${statusResult.summary.risky}
Unknown: ${statusResult.summary.unknown}
Catch-all: ${statusResult.summary.catch_all}

Daily credits are used first, then instant credits.
              `.trim();
              
              showPageAlert(summaryMessage, 'success');
              
              // Refresh credits
              fetchCredits();
              
              // Refresh data to show updated verification status
              const resumedDomainIds = searchParams.get('resumedDomainIds');
              const updatedData = await ApiService.getValidationData(sessionId, resumedDomainIds || undefined);
              setData(updatedData);
              
              // Clear selection
              setSelectedDomains(new Set());
              setSelectAll(false);
            } else {
              // Permanent error - stop polling
              if (pollInterval) clearInterval(pollInterval);
              setIsValidating(false);
              setValidationProgress('');
              showPageAlert(`Validation failed: ${statusResult.message || statusResult.error || 'Unknown error'}`, 'error');
            }
          } catch (err: any) {
            console.error('Error checking validation status:', err);
            // For network errors, continue polling (task may still be processing)
            if (err.message && (err.message.includes('521') || err.message.includes('502') || err.message.includes('503') || err.message.includes('504') || err.message.includes('ECONNREFUSED') || err.message.includes('ETIMEDOUT'))) {
              setValidationProgress(`⚠️ Temporary API error - continuing to check status...`);
              console.warn('[Validation] Network error, continuing to poll:', err.message);
              // Don't stop polling for temporary errors
            } else {
              // Permanent error - stop polling
              if (pollInterval) clearInterval(pollInterval);
              setIsValidating(false);
              setValidationProgress('');
              showPageAlert(`Error checking validation status: ${err.message || 'Unknown error'}`, 'error');
            }
          }
        }, 5000); // Poll every 5 seconds
        
      } else {
        setIsValidating(false);
        setValidationProgress('');
        showPageAlert('Failed to create validation task', 'error');
      }
      
    } catch (err: any) {
      console.error('Error validating emails:', err);
      if (pollInterval) clearInterval(pollInterval);
      setError(err.message || 'Failed to validate emails. Please try again.');
      showPageAlert(`Error: ${err.message || 'Failed to validate emails'}`, 'error');
      setIsValidating(false);
      setValidationProgress('');
    }
  };

  const handleScheduleValidation = () => {
    if (selectedDomains.size === 0) {
      showPageAlert('Please select at least one domain to schedule.');
      return;
    }
    
    // Set default name if empty
    if (!scheduleName) {
      const date = new Date().toLocaleDateString();
      setScheduleName(`Validation Task ${date}`);
    }
    
    setShowScheduleModal(true);
  };

  const confirmSchedule = async () => {
    setShowScheduleModal(false);
    setIsValidating(true);
    setValidationProgress('Preparing emails for scheduling...');
    
    try {
      // Get selected domain objects
      const selectedDomainObjects = data.filter(d => selectedDomains.has(d.id));
      const domains = selectedDomainObjects.map(d => d.website).filter(Boolean);
      
      // Fetch all emails for selected domains
      const emailPromises = domains.map(domain => 
        ApiService.getEmailsByDomain(domain, sessionId || undefined)
      );
      const emailArrays = await Promise.all(emailPromises);
      const allEmails = emailArrays.flat().filter((email, index, self) => 
        email && self.indexOf(email) === index
      );

      if (allEmails.length === 0) {
        showPageAlert('No emails found for selected domains.');
        setIsValidating(false);
        return;
      }

      const result = await ApiService.scheduleValidation({
        emails: allEmails,
        sessionId: sessionId!,
        name: scheduleName,
        dailyLimit: dailyLimit
      });

      showPageAlert(result.message, 'success');
      
      // Refresh data
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const updatedData = await ApiService.getValidationData(sessionId!, resumedDomainIds || undefined);
      setData(updatedData);
      
      setSelectedDomains(new Set());
      setSelectAll(false);
      
    } catch (err: any) {
      console.error('Error scheduling validation:', err);
      showPageAlert(`Error: ${err.message || 'Failed to schedule validation'}`, 'error');
    } finally {
      setIsValidating(false);
      setValidationProgress('');
    }
  };

  const handleExportAllEmails = async () => {
    if (!sessionId) {
      showPageAlert('Session ID not found.', 'error');
      return;
    }
    try {
      setIsLoading(true);
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const blob = await ApiService.exportAllEmails(sessionId, resumedDomainIds || undefined);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `all_emails_${sessionId}_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error exporting all emails:', err);
      showPageAlert(`Failed to export emails: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportAllExcel = async () => {
    if (!sessionId) {
      showPageAlert('Session ID not found.', 'error');
      return;
    }
    try {
      setIsLoading(true);
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const blob = await ApiService.exportOutreachExcel(sessionId, resumedDomainIds || undefined);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `outreach_full_results_${sessionId}_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error exporting to Excel:', err);
      showPageAlert(`Failed to export Excel: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /> <Typography>Loading validation data...</Typography></Box>;
  }

  if (error) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Email Validation: {data.length}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Validate and manage domain email lists
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              color={selectedDomains.size === data.length ? "success" : "primary"}
              onClick={handleSelectAll}
              sx={{
                fontWeight: 'bold',
                textTransform: 'none',
              }}
            >
              {selectedDomains.size === data.length ? "DESELECT ALL" : `SELECT ALL ${data.length} IN QUERY`}
            </Button>
            {selectedDomains.size > 0 && (
              <Typography variant="body2" color={selectedDomains.size === data.length ? "success.main" : "text.secondary"} sx={{ fontWeight: 600 }}>
                ({selectedDomains.size} entries selected)
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Hide BACK button for resumed sessions (tagging is already done) */}
          {!searchParams.get('resumedDomainIds') && (
            <Button variant="outlined" startIcon={<ArrowBackIcon />} size="small" onClick={handleBack}>
              BACK
            </Button>
          )}
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => {
              const resumedDomainIds = searchParams.get('resumedDomainIds');
              const url = resumedDomainIds 
                ? `/outreach/email-results?session=${sessionId}&resumedDomainIds=${resumedDomainIds}`
                : `/outreach/email-results?session=${sessionId}`;
              navigate(url);
            }}
          >
            VIEW EMAIL RESULTS
          </Button>
           <Button 
            variant="contained" 
            color="success"
            size="small" 
            startIcon={<DownloadIcon />}
            onClick={handleExportAllExcel}
            disabled={isLoading}
            sx={{ fontWeight: 'bold' }}
          >
            EXPORT ALL (EXCEL)
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            startIcon={<DownloadIcon />}
            onClick={handleExportAllEmails}
            disabled={isLoading}
          >
            EXPORT ALL (CSV)
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => navigate('/outreach/scheduled-tasks')}
          >
            VIEW SCHEDULED TASKS
          </Button>
        </Box>
      </Box>

      {pageAlert && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={pageAlert.severity} onClose={() => setPageAlert(null)}>
            {pageAlert.message}
          </Alert>
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card><CardContent><Typography color="text.secondary" variant="body2" gutterBottom>Total Domains</Typography><Typography variant="h4" sx={{ fontWeight: 'bold' }}>{summaryStats.totalDomains}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card><CardContent><Typography color="text.secondary" variant="body2" gutterBottom>Total Emails</Typography><Typography variant="h4" sx={{ fontWeight: 'bold' }}>{summaryStats.totalEmails}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography color="text.secondary" variant="body2" gutterBottom>Reoon Daily Credits</Typography>
                <Tooltip title="Refresh credits">
                  <Button 
                    size="small" 
                    onClick={fetchCredits} 
                    disabled={credits.loading}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    <RefreshIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {credits.loading ? '...' : credits.daily.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>Reoon Instant Credits</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {credits.loading ? '...' : credits.instant.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Validation progress display */}
      {isValidating && validationProgress && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" icon={<CircularProgress size={20} />}>
            {validationProgress}
          </Alert>
        </Box>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="contained" startIcon={<ValidateIcon />} onClick={handleValidateSelected} disabled={selectedDomains.size === 0 || isValidating}>
          {isValidating ? 'VALIDATING...' : 'VALIDATE'}
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleSaveForLater} disabled={selectedDomains.size === 0 || isSaving || isValidating}>
          {isSaving ? 'SAVING SELECTED...' : 'SAVE SELECTED FOR LATER'}
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleSaveAllForLater} disabled={data.length === 0 || isSaving || isValidating}>
          {isSaving ? 'SAVING ALL...' : 'SAVE ALL FOR LATER'}
        </Button>
        <Button variant="outlined" color="error" onClick={handleDeleteSelected} disabled={selectedDomains.size === 0 || isSaving || isValidating || isDeleting}>
          {isDeleting ? 'DELETING...' : 'DELETE'}
        </Button>
        <Button 
          variant="contained" 
          color="success" 
          onClick={handleScheduleValidation} 
          disabled={selectedDomains.size === 0 || isValidating}
        >
          SCHEDULE THROTTLED
        </Button>
        <Box sx={{ flexGrow: 1 }} />
      </Box>

      {(isValidating || isSaving) && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isValidating ? 'Validating emails...' : 'Saving domains for later...'}
          </Typography>
        </Box>
      )}

      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 3
      }}>
        <Box sx={{ 
          overflow: 'auto',
          width: '100%',
          maxWidth: '100%',
          maxHeight: 'none',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}>
          <Paper sx={{ width: '100%', overflow: 'visible', boxShadow: 'none' }}>
            <TableContainer sx={{ overflow: 'visible' }}>
              <Table stickyHeader sx={{ minWidth: 2500, '& .MuiTableCell-root': { padding: '6px 6px' } }}>
            <TableHead>
              <TableRow>
                    {/* Match old domains table order: Checkbox first, then all company columns in order, Email Count at the end */}
                    <TableCell sx={{ minWidth: 50, fontWeight: 'bold', backgroundColor: '#fff' }}>
                      <Checkbox checked={selectAll} onChange={handleSelectAll} size="small" />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('website')}>
                        Website
                        {sortField === 'website' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>
                        Company Name
                        {sortField === 'name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('clean_name')}>
                        Clean Name
                        {sortField === 'clean_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('note')}>
                        Notes
                        {sortField === 'note' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 300, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('description')}>
                        Description
                        {sortField === 'description' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('unsubscribe')}>
                        Unsubscribe
                        {sortField === 'unsubscribe' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('in_mailer')}>
                        Inmailer
                        {sortField === 'in_mailer' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('state_name')}>
                        State
                        {sortField === 'state_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('location')}>
                        Location
                        {sortField === 'location' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('country_name')}>
                        Country
                        {sortField === 'country_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('company_address')}>
                        Company Address
                        {sortField === 'company_address' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('snov_id')}>
                        Snov ID
                        {sortField === 'snov_id' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('industry_tag_name')}>
                        Industry Tag
                        {sortField === 'industry_tag_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('industry_tag_two_name')}>
                        Industry Tag Two
                        {sortField === 'industry_tag_two_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('industry_tag_three_name')}>
                        Industry Tag Three
                        {sortField === 'industry_tag_three_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('industry_tag_four_name')}>
                        Industry Tag Four
                        {sortField === 'industry_tag_four_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                     <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                       <Tooltip title="Deliverable" arrow>
                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('deliverable')}>
                           Del
                           {sortField === 'deliverable' && (
                             <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                               {sortDirection === 'asc' ? '↑' : '↓'}
                             </span>
                           )}
                         </Box>
                       </Tooltip>
                     </TableCell>
                     <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                       <Tooltip title="Risky" arrow>
                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('risky')}>
                           Ris
                           {sortField === 'risky' && (
                             <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                               {sortDirection === 'asc' ? '↑' : '↓'}
                             </span>
                           )}
                         </Box>
                       </Tooltip>
                     </TableCell>
                     <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                       <Tooltip title="Undeliverable" arrow>
                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('undeliverable')}>
                           Und
                           {sortField === 'undeliverable' && (
                             <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                               {sortDirection === 'asc' ? '↑' : '↓'}
                             </span>
                           )}
                         </Box>
                       </Tooltip>
                     </TableCell>
                     <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                       <Tooltip title="Bounce" arrow>
                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('bounce')}>
                           Bou
                           {sortField === 'bounce' && (
                             <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                               {sortDirection === 'asc' ? '↑' : '↓'}
                             </span>
                           )}
                         </Box>
                       </Tooltip>
                     </TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('Linkedin')}>
                        LI
                        {sortField === 'Linkedin' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('employees_onlinkedin')}>
                        Employees on LinkedIn
                        {sortField === 'employees_onlinkedin' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('linkedin_id')}>
                        LinkedIn ID
                        {sortField === 'linkedin_id' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('twitter')}>
                        TW
                        {sortField === 'twitter' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('Facebook')}>
                        FB
                        {sortField === 'Facebook' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('ig')}>
                        IG
                        {sortField === 'ig' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('Youtube')}>
                        YT
                        {sortField === 'Youtube' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('license')}>
                        License
                        {sortField === 'license' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('phone')}>
                        Phone
                        {sortField === 'phone' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('unknown')}>
                        Unknown
                        {sortField === 'unknown' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('not_verified')}>
                        Not Verified
                        {sortField === 'not_verified' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('suspicious')}>
                        Suspicious
                        {sortField === 'suspicious' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('type_')}>
                        Type
                        {sortField === 'type_' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>
                        Status
                        {sortField === 'status' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('emailCount')}>
                        Email Count
                        {sortField === 'emailCount' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.slice(0, displayCount).map((domain: any) => (
                  <TableRow key={domain.id} hover>
                      <TableCell>
                        <Checkbox checked={selectedDomains.has(domain.id)} onChange={() => handleSelectDomain(domain.id)} size="small" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>{renderCompactLink(domain.website, 'website')}</TableCell>
                      <TableCell>{domain.name || '-'}</TableCell>
                      <TableCell>{domain.clean_name || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={String(domain.note || '')} arrow>
                          <span>{truncateText(String(domain.note || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={String(domain.description || '')} arrow>
                          <span>{truncateText(String(domain.description || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{domain.unsubscribe ?? '-'}</TableCell>
                      <TableCell align="right">{domain.in_mailer ?? '-'}</TableCell>
                      <TableCell>{domain.state_name || '-'}</TableCell>
                      <TableCell>{domain.location || '-'}</TableCell>
                      <TableCell>{domain.country_name || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={String(domain.company_address || '')} arrow>
                          <span>{truncateText(String(domain.company_address || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{domain.snov_id || '-'}</TableCell>
                      <TableCell>{domain.industry_tag_name || '-'}</TableCell>
                      <TableCell>{domain.industry_tag_two_name || '-'}</TableCell>
                      <TableCell>{domain.industry_tag_three_name || '-'}</TableCell>
                      <TableCell>{domain.industry_tag_four_name || '-'}</TableCell>
                      <TableCell align="right">{domain.deliverable ?? '-'}</TableCell>
                      <TableCell align="right">{domain.risky ?? '-'}</TableCell>
                      <TableCell align="right">{domain.undeliverable ?? '-'}</TableCell>
                      <TableCell align="right">{domain.bounce ?? '-'}</TableCell>
                      <TableCell>{renderCompactLink(domain.Linkedin, 'linkedin')}</TableCell>
                      <TableCell>{domain.employees_onlinkedin || '-'}</TableCell>
                      <TableCell>{domain.linkedin_id || '-'}</TableCell>
                      <TableCell>{renderCompactLink(domain.twitter, 'twitter')}</TableCell>
                      <TableCell>{renderCompactLink(domain.Facebook, 'Facebook')}</TableCell>
                      <TableCell>{renderCompactLink(domain.ig, 'ig')}</TableCell>
                      <TableCell>{renderCompactLink(domain.Youtube, 'Youtube')}</TableCell>
                      <TableCell>{domain.license || '-'}</TableCell>
                      <TableCell>{domain.phone || '-'}</TableCell>
                      <TableCell align="right">{domain.unknown ?? '-'}</TableCell>
                      <TableCell align="right">{domain.not_verified ?? '-'}</TableCell>
                      <TableCell>{domain.suspicious || '-'}</TableCell>
                      <TableCell>{domain.type_ || '-'}</TableCell>
                      <TableCell>{domain.status || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{domain.emailCount || 0}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
          </Paper>
        </Box>
      </Box>
      
      {/* Load More Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2,
        mb: 3, 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {Math.min(displayCount, data.length)} of {data.length}
        </Typography>
        
        {isLoadingMore ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1
          }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : displayCount < data.length ? (
          <button 
            onClick={handleLoadMore}
            style={{
              padding: '6px 16px',
              backgroundColor: 'transparent',
              color: '#1976d2',
              border: '1px solid #1976d2',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
              (e.target as HTMLButtonElement).style.color = 'white';
            }}
            onMouseOut={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
              (e.target as HTMLButtonElement).style.color = '#1976d2';
            }}
          >
            Load More
          </button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No more records
          </Typography>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete {selectedDomains.size} selected domain(s)? 
            This will permanently delete the domains and all associated emails. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirmation(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Back Navigation Warning */}
      <Dialog
        open={showBackWarning}
        onClose={() => setShowBackWarning(false)}
        aria-labelledby="back-dialog-title"
        aria-describedby="back-dialog-description"
      >
        <DialogTitle id="back-dialog-title">
          Leaving validation?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="back-dialog-description">
            You haven’t validated or saved the selected domains yet. Going back will discard your current progress and any API credits already charged by GMO or Reoon will be wasted. Validate or use “Save for Later” before leaving if you want to preserve your work.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBackWarning(false)} color="primary">
            Stay here
          </Button>
          <Button onClick={confirmBack} color="error" variant="contained" autoFocus>
            Leave anyway
          </Button>
        </DialogActions>
      </Dialog>
      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onClose={() => setShowScheduleModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>Schedule Throttled Validation</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f7fa', borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">Task Summary</Typography>
            <Typography variant="body2">
              Emails to validate: <strong>{data.filter(d => selectedDomains.has(d.id)).reduce((sum, d) => sum + (d.email_count || d.emailCount || 0), 0).toLocaleString()}</strong>
            </Typography>
            <Typography variant="body2">
              Daily Limit: <strong>{dailyLimit.toLocaleString()}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'success.main' }}>
              Estimated Duration: {Math.ceil(data.filter(d => selectedDomains.has(d.id)).reduce((sum, d) => sum + (d.email_count || d.emailCount || 0), 0) / dailyLimit)} day(s)
            </Typography>
          </Box>

          <Box sx={{ mb: 3, p: 2, bgcolor: '#fff4e5', borderRadius: 2, borderLeft: '4px solid #ffa000' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#b27000' }}>Capacity Check</Typography>
              <Tooltip title="Free credits reset every day at Midnight PST (07:00 UTC)">
                <Typography variant="caption" sx={{ color: '#b27000', cursor: 'help', textDecoration: 'underline' }}>
                  Resets at Midnight PST
                </Typography>
              </Tooltip>
            </Box>
            {validationStats ? (
              <>
                <Typography variant="body2">
                  Used Today: <strong>{validationStats.creditsUsedToday.toLocaleString()}</strong> / {validationStats.dailyLimit.toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  Remaining Today: <strong>{validationStats.remaining.toLocaleString()}</strong>
                </Typography>
                {validationStats.remaining < 500 && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    ⚠️ Capacity is low. Most of this task will likely start tomorrow.
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2">Loading capacity stats...</Typography>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Task Name</Typography>
              <input 
                type="text" 
                value={scheduleName} 
                onChange={(e) => setScheduleName(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  marginTop: '4px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="e.g. Weekly Validation"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Daily Credit Limit (Conservative: 4500)</Typography>
              <input 
                type="number" 
                value={dailyLimit} 
                onChange={(e) => setDailyLimit(parseInt(e.target.value) || 4500)}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  marginTop: '4px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Total Reoon daily limit is shared across all users. Erring on the side of conservative (e.g. 4000) avoids over-limit charges.
              </Typography>
            </Grid>
          </Grid>

          {/* Day-by-day Timeline */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">Projected Timeline</Typography>
            <Box sx={{ borderLeft: '2px solid #1976d2', ml: 1, pl: 2 }}>
              {(() => {
                const totalEmails = data.filter(d => selectedDomains.has(d.id)).reduce((sum, d) => sum + (d.email_count || d.emailCount || 0), 0);
                const remainingToday = validationStats?.remaining || 0;
                const timeline = [];
                let processed = 0;
                let day = 0;

                // Day 1 (Today)
                const day1Count = Math.min(totalEmails, remainingToday);
                if (day1Count > 0) {
                  timeline.push({ day: 'Today', count: day1Count, time: 'Starting now' });
                  processed += day1Count;
                }

                // Subsequent Days
                while (processed < totalEmails) {
                  day++;
                  const remaining = totalEmails - processed;
                  const dayCount = Math.min(remaining, dailyLimit);
                  const date = new Date();
                  date.setDate(date.getDate() + day);
                  timeline.push({ 
                    day: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }), 
                    count: dayCount,
                    time: '12:00 AM'
                  });
                  processed += dayCount;
                }

                return timeline.map((item, idx) => (
                  <Box key={idx} sx={{ mb: 1.5, position: 'relative' }}>
                    <Box sx={{ 
                      position: 'absolute', 
                      left: '-25px', 
                      top: '4px', 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      bgcolor: idx === 0 ? 'success.main' : '#1976d2' 
                    }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {item.day}: <span style={{ color: '#666' }}>{item.count.toLocaleString()} emails</span>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Scheduled to run at {item.time}
                    </Typography>
                  </Box>
                ));
              })()}
            </Box>
            <Box sx={{ mt: 1, p: 1, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9' }}>
              <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                ✅ Strategy: Free Credits Only (Zero Cost)
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #eee' }}>
          <Button onClick={() => setShowScheduleModal(false)} sx={{ color: 'text.secondary' }}>CANCEL</Button>
          <Button onClick={confirmSchedule} variant="contained" color="success" size="large" sx={{ px: 4 }}>
            CONFIRM SCHEDULE
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default OutreachValidationPage;

